import axios from 'axios'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import * as pdf from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.entry'
import { TextItem } from 'pdfjs-dist/types/src/display/api'

import { FileManagerFile } from '@/features/FileManager/fileManagerSlice'

pdf.GlobalWorkerOptions.workerSrc = pdfWorker

/**
 * Get text content from a pdf file.
 * Based on https://github.com/mozilla/pdf.js/blob/master/examples/node/getinfo.js
 * @param filePath
 */
export async function getPDFContent(filePath) {
  const result: string[] = []

  const loadingTask = pdf.getDocument(filePath)

  const mainPromise = loadingTask.promise.then(function (doc) {
    const numPages = doc.numPages

    let lastPromise // will be used to chain promises
    lastPromise = doc.getMetadata().then(function (data) {
      result.push(JSON.stringify(data.info, null, 2))

      if (data.metadata) {
        result.push(JSON.stringify(data.metadata.getAll(), null, 2))
      }
    })

    const loadPage = function (pageNum) {
      return doc.getPage(pageNum).then(function (page) {
        return page.getTextContent().then(function (content) {
          const strings = content.items.map(function (item) {
            return (item as TextItem).str
          })
          result.push(...strings)

          // Release page resources.
          page.cleanup()
        })
      })
    }

    // Loading of the first page will wait on metadata and subsequent loadings
    // will wait on the previous pages.
    for (let i = 1; i <= numPages; i++) {
      lastPromise = lastPromise.then(loadPage.bind(null, i))
    }
    return lastPromise
  })

  await mainPromise

  return result.join(' ')
}

export const isS3File = (filePath) => {
  return !!filePath.includes('s3.amazonaws.com')
}

const prepareHeaders = (file) => {
  // Adds JWT auth headers only for non S3 requests,
  // as adding them will make the request to S3 fail.
  const { downloadPath, download_url } = file
  const url = downloadPath || download_url
  const headers = {
    'content-type': 'multipart/form-data',
  }

  // Add JWT token for non S3 requests
  if (!isS3File(url)) {
    headers['Authorization'] = `JWT ${localStorage.getItem('jwt')}`
  }

  return headers
}

export const openFile = async (file: FileManagerFile) => {
  const { downloadPath, download_url } = file
  const url = downloadPath || download_url
  const headers = prepareHeaders(file)
  const { data } = await axios.get(url, {
    headers,
    responseType: 'blob',
    withCredentials: true,
  })
  return data
}

export const createFile = async (file: FileManagerFile) => {
  const { fileName, original_filename } = file
  const finalFileName = fileName || original_filename
  const data = await openFile(file)

  return new File([data], finalFileName, {
    type: `application/${finalFileName?.split('.')[1].toUpperCase()}`,
  })
}

export const downloadFile = async (file: FileManagerFile) => {
  const newFile = await createFile(file)
  saveAs(newFile)
}

export const downloadManyFiles = async (files: FileManagerFile[]) => {
  const zip = new JSZip()

  files.forEach((file) => {
    const f = createFile(file)
    zip.file(file.fileName, f)
  })

  zip.generateAsync({ type: 'blob' }).then(function (content) {
    saveAs(content, 'download.zip')
  })
}

export const openMaxViewer = (
  applicationExternalId: string | string[],
  files: Partial<FileManagerFile>[]
) => {
  if (files.length) {
    const fileIds = files.map((file) => file.id)
    const searchParams = new URLSearchParams()
    fileIds.forEach((id) => searchParams.append('id', `${id}`))
    const urlParams = searchParams.toString()
    window.open(`/applications/${applicationExternalId}/documents-viewer?${urlParams}`, '_blank')
  }
}

const checkMaxViewerSupportedFiles = (files) => {
  /**
   * Check if the files are supported by the max viewer
   * @param files the files to check
   * @returns an object with the supported files and the unsupported files
   */
  const supportedExtensions = ['pdf', 'gif', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'xml', 'json']
  const supportedFiles = files.filter((file) => {
    const extension = file.fileName.split('.')?.slice(-1)[0]
    return supportedExtensions.includes(extension?.toLowerCase())
  })
  const unsupportedFiles = files.filter((file) => {
    const extension = file.fileName.split('.')?.slice(-1)[0]
    return !supportedExtensions.includes(extension?.toLowerCase())
  })
  return { supportedFiles, unsupportedFiles }
}

export const openFiles = async (
  files: FileManagerFile[],
  applicationExternalId: string | string[]
) => {
  const { supportedFiles, unsupportedFiles } = checkMaxViewerSupportedFiles(files)
  openMaxViewer(applicationExternalId, supportedFiles)
  unsupportedFiles.forEach((file) => window.open(file.filePath, '_blank'))
}

export const handleOpenFile = async (file, setIsLoading = null) => {
  if (setIsLoading) {
    setIsLoading(true)
  }
  if (isS3File(file.downloadPath)) {
    window.open(file.downloadPath, '_blank')
  } else {
    // For non S3 download links (CX files that require preprocesing),
    // we need to make a request with valid auth headers to the API
    const blob = await openFile(file)
    const _url = window.URL.createObjectURL(blob)
    window.open(_url, '_blank').focus()
  }
  if (setIsLoading) {
    setIsLoading(false)
  }
}
